import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, BadgeCheck, Check, Heart, MapPin, Play, Plus, Radio, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { creators, PlatformBadge, imgUtil, Reveal } from "@/components/marketing/kit";
import { profileFor } from "@/components/marketing/content";

const Bars = ({ title, rows }) => (
  <div className="cm-aud-block">
    <h5>{title}</h5>
    {rows.map(([label, val]) => (
      <div className="cm-bar" key={label}>
        <span className="cm-bar-label">{label}</span>
        <div className="cm-bar-track"><motion.i initial={{ width: 0 }} whileInView={{ width: `${val}%` }} viewport={{ once: true }} transition={{ duration: 0.8, ease: [0.21, 0.5, 0.3, 1] }} /></div>
        <span className="cm-bar-val">{val}%</span>
      </div>
    ))}
  </div>
);

export default function CreatorProfile() {
  const { id } = useParams();
  const creator = creators.find((c) => c.id === id);
  const [live, setLive] = useState({ status: "idle" });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!creator) return;
    window.scrollTo(0, 0);
    setLive({ status: "loading" });
    let active = true;
    api.get(`/social/${creator.platform}?handle=${encodeURIComponent(creator.handle)}`)
      .then((r) => { if (active) setLive({ status: r.data?.available ? "live" : "sample", data: r.data }); })
      .catch(() => { if (active) setLive({ status: "sample" }); });
    return () => { active = false; };
  }, [creator]);

  if (!creator) return <Navigate to="/creators" replace />;

  const p = profileFor(creator);
  const liveFollowers = live.status === "live" && live.data?.followers;
  const usd = (n) => `$${n.toLocaleString()}`;

  return (
    <main data-testid="creator-profile-page">
      <section className="mk-sec" style={{ paddingBottom: 0 }}>
        <div className="mk-wrap">
          <Link to="/creators" className="cp-back" data-testid="creator-profile-back"><ArrowLeft size={16} /> Back to creators</Link>

          <div className="cp-hero">
            <img src={imgUtil.wide(creator.cover)} alt="" />
            <div className="cp-hero-ov" />
            <span className={`cm-live ${live.status}`} data-testid="creator-profile-live-badge">
              <Radio size={12} /> {live.status === "loading" ? "Checking live data…" : live.status === "live" ? "LIVE DATA" : "Sample data"}
            </span>
          </div>

          <div className="cp-id">
            <div className="cm-ava"><img src={imgUtil.sq(creator.avatar)} alt="" /></div>
            <div className="cm-id-text">
              <h3 data-testid="creator-profile-name">{creator.name} <BadgeCheck size={18} fill="#00F2FE" stroke="#0A0D14" /></h3>
              <span className="cm-handle">@{creator.handle}</span>
              <div className="cm-id-meta"><PlatformBadge platform={creator.platform} /> <span><MapPin size={13} /> {creator.location}</span> <span>{creator.niche}</span></div>
            </div>
            <div className="cm-fit"><b>{creator.fit}</b><small>AI FIT SCORE</small></div>
          </div>

          <p className="cm-bio" style={{ maxWidth: 760 }}>{p.bio}</p>

          <div className="cm-metrics" style={{ maxWidth: 760 }}>
            {p.metrics.map((m, i) => (
              <div key={m.label} data-testid={`profile-metric-${i}`}>
                <b>{i === 0 && liveFollowers ? (liveFollowers >= 1e6 ? `${(liveFollowers / 1e6).toFixed(1)}M` : `${Math.round(liveFollowers / 1e3)}K`) : m.value}</b>
                <small>{m.label}{i === 0 && liveFollowers ? " · live" : ""}</small>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mk-sec" style={{ paddingTop: "clamp(40px,6vw,70px)", paddingBottom: 0 }}>
        <div className="mk-wrap">
          <Reveal><h2 className="mk-h2 cp-h" data-testid="creator-profile-reels-title">Recent reels</h2></Reveal>
          <div className="cp-reels" data-testid="creator-profile-reels">
            {p.reels.map((r, i) => (
              <motion.div className="cm-reel" key={i} data-testid={`profile-reel-${i}`}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.45, delay: (i % 6) * 0.05 }}>
                <img src={imgUtil.wide(r.thumb)} alt="" loading="lazy" />
                <div className="cm-reel-ov" />
                <span className="cm-reel-play"><Play size={14} fill="#fff" stroke="none" /></span>
                <div className="cm-reel-stats"><span><Play size={11} fill="#fff" stroke="none" /> {r.views}</span><span><Heart size={11} fill="#FF2B70" stroke="none" /> {r.likes}</span></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="mk-sec" style={{ paddingBottom: 0 }}>
        <div className="mk-wrap">
          <Reveal><h2 className="mk-h2 cp-h" data-testid="creator-profile-rates-title">Rates</h2></Reveal>
          <p className="cp-sub">Indicative pricing (USD). Final rates are negotiated per brief inside HypeFlow.</p>
          <div className="cp-rates" data-testid="creator-profile-rates">
            {p.rates.map((rt, i) => (
              <motion.div className={`cp-rate${rt.popular ? " pop" : ""}`} key={rt.label} data-testid={`profile-rate-${i}`}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.45, delay: i * 0.06 }}>
                {rt.popular && <span className="cp-rate-tag">Most booked</span>}
                {rt.save && <span className="cp-rate-save">{rt.save}</span>}
                <span className="cp-rate-label">{rt.label}</span>
                <div className="cp-rate-price"><b>{usd(rt.price)}</b><small>{rt.unit}</small></div>
                <p className="cp-rate-desc"><Check size={13} /> {rt.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="mk-sec" style={{ paddingTop: "clamp(40px,6vw,70px)", paddingBottom: 0 }}>
        <div className="mk-wrap">
          <Reveal><h2 className="mk-h2 cp-h" data-testid="creator-profile-audience-title">Audience breakdown</h2></Reveal>
          <div className="cp-audience" data-testid="creator-profile-audience">
            <Bars title="Age" rows={p.audience.age} />
            <Bars title="Gender" rows={p.audience.gender} />
            <Bars title="Top geos" rows={p.audience.geo} />
          </div>
        </div>
      </section>

      <section className="mk-sec"><div className="mk-wrap"><div className="mk-cta">
        <span className="mk-pill" style={{ margin: "0 auto" }}><Sparkles size={14} /> AI-matched for your brief</span>
        <h2>Work with {creator.name.split(" ")[0]}</h2>
        <p>Shortlist this creator and start a brief — HypeFlow handles outreach, approvals and reporting end to end.</p>
        <div className="cm-actions" style={{ maxWidth: 520, margin: "6px auto 0" }}>
          <button className="mk-btn ghost" onClick={() => setSaved(true)} data-testid="creator-profile-shortlist">
            <Plus size={16} /> {saved ? "Added to shortlist" : "Add to shortlist"}
          </button>
          <Link to="/login?mode=register" className="mk-btn primary" data-testid="creator-profile-start-campaign">Start a campaign <ArrowRight size={16} /></Link>
        </div>
      </div></div></section>
    </main>
  );
}
